import Phaser from 'phaser';
import * as Tone from 'tone';
import { colors } from 'Phaser/config';
import {
  EventBus,
  ReactToPhaserListeners,
  NotifyReactEvents,
  RemoveListener,
} from 'Phaser/EventBus2';
import Instruction, { InstructionPosition } from 'Phaser/GameObjects/Instruction';
import { Timestamp } from 'Types/ExerciseData';
import { isCapacitorNative } from 'Utils/Constants';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { feedbackAdvanceDelayMs } from 'Utils/feedbackTiming';
import { createActor, type ActorRefFrom } from 'xstate';
import { exerciseMachine, type ExerciseMachine } from 'Phaser/ExerciseMachine/exerciseMachine';
import { buildSceneActions } from 'Phaser/ExerciseMachine/buildSceneActions';
import type { ExerciseEvent, ExercisePhase } from 'Phaser/ExerciseMachine/exerciseMachine.types';
import type { IExerciseSceneFacade } from 'Phaser/ExerciseMachine/IExerciseSceneFacade';

const FEEDBACK_TYPES = [
  'great',
  'great_job',
  'good_job',
  'nice',
  'excellent',
  'nicely_done',
  'well_done',
  'perfect',
];

function feedbackKeyToFilename(key: string): string {
  const match = key.match(/^feedback(\d+)_0(\d+)$/);
  if (!match) return key;
  const typeIdx = parseInt(match[1], 10);
  const variantIdx = match[2];
  const typeName = FEEDBACK_TYPES[typeIdx];
  return typeName ? `${typeName}_0${variantIdx}` : key;
}

// MUS-113: on web, if a feedback clip's duration cannot be read (e.g. the
// HTML5 audio fallback backend, or a decode miss) fall back to the pre-fix
// 1000ms so advance timing is never worse than it was before the fix.
const FEEDBACK_DURATION_FALLBACK_SECS = 1;

export interface IObjective {
  text?: string;
  instructionWaitTime?: number;
  videoSkipDestination?: Timestamp;
  videoCompleteDestination?: Timestamp;
  narrationStartTimestamp?: Timestamp;
  narrationEndTimestamp?: Timestamp;
}

export interface IConfig<Objective extends IObjective> {
  chapterIdx?: number;
  levelNumber?: number;
  playbackSpeed?: number;
  objectives: Objective[];
  firstObjectiveAppearDelay?: number;
}

export default abstract class ExerciseBase<
  Objective extends IObjective,
  Config extends IConfig<Objective>,
>
  extends Phaser.Scene
  implements IExerciseSceneFacade
{
  active: boolean = false;
  instruction: Instruction | null = null;
  numFailedAttempts: number = 0;
  failMessage: Phaser.GameObjects.Text | null = null;
  toastMessage: Phaser.GameObjects.Text | null = null;
  debugText: Phaser.GameObjects.Text | null = null;
  paused: boolean = false;
  config?: Config;
  currentObjective?: number;
  waitToast: boolean = false;

  registeredKeys: Map<string, Phaser.Input.Keyboard.Key> = new Map();
  skipped: boolean = false;
  videoResumeTimeout?: Phaser.Time.TimerEvent;

  protected boundCallbacks: Map<string, (...args: never[]) => void> = new Map();

  // ---- State Machine ----
  private exerciseActor: ActorRefFrom<ExerciseMachine> | null = null;

  protected dispatchExerciseEvent(event: ExerciseEvent): void {
    const phase = this.exerciseActor?.getSnapshot()?.value;
    console.log(`[EXERCISE-DBG] dispatchExerciseEvent type=${event.type} currentPhase=${phase}`);
    this.exerciseActor?.send(event);
  }

  getExercisePhase(): ExercisePhase | undefined {
    return this.exerciseActor?.getSnapshot()?.value as ExercisePhase | undefined;
  }

  // ---- Helpers ----

  protected getValidObjectiveIndex(): number {
    if (!this.config?.objectives?.length) return 0;
    if (this.currentObjective === undefined || this.currentObjective < 0) {
      return 0;
    }
    return Math.min(this.currentObjective, this.config.objectives.length - 1);
  }

  protected getCurrentObjective(): Objective | undefined {
    if (!this.config?.objectives?.length) return undefined;
    return this.config.objectives[this.getValidObjectiveIndex()];
  }

  protected isLastObjective(): boolean {
    if (!this.config?.objectives?.length) return true;
    return this.getValidObjectiveIndex() === this.config.objectives.length - 1;
  }

  private timestampToSeconds(stamp: Timestamp): number {
    const [min, sec] = stamp.split(':').map(parseFloat);
    return min * 60 + sec;
  }

  showInstruction(instruction: string, position: InstructionPosition = 'Top'): void {
    this.instruction = new Instruction(this, instruction, position);
  }

  debugLog(text: string): void {
    if (process.env.REACT_APP_NODE_ENV === 'prd' || process.env.REACT_APP_NODE_ENV === 'stg')
      return;
    if (!this.debugText)
      this.debugText = this.add.text(200, 100, text, {
        color: '#000',
        fontSize: 22,
      });
    else this.debugText.setText(text);
  }

  setPlaybackRate({ rate: _rate }: { rate: number }) {}

  // ==================================================================
  // Scene lifecycle
  // ==================================================================

  init(config: Config): void {
    this.events.once('shutdown', this.unload, this);

    // Clear stale listeners
    RemoveListener('StartExercise');
    RemoveListener('PauseExercise');
    RemoveListener('MidiOn');
    RemoveListener('MidiOff');
    RemoveListener('SkipExercise');
    RemoveListener('UnloadExercise');
    RemoveListener('ChangePlaybackRate');
    RemoveListener('ToggleExercise');
    RemoveListener('ResumeExercise');
    RemoveListener('UpdateObjective');
    this.boundCallbacks = new Map();

    const bind = <T>(name: string, fn: (arg: T) => void): ((arg: T) => void) => {
      this.boundCallbacks.set(name, fn as (...args: never[]) => void);
      return fn;
    };

    // Store config and reset state
    this.config = config;
    this.active = true;
    this.paused = false;
    this.currentObjective = undefined;
    this.numFailedAttempts = 0;
    this.skipped = false;

    // ---- Spawn the state machine ----
    const facade = this.createFacade();
    const dispatchEvent = (event: ExerciseEvent) => this.dispatchExerciseEvent(event);
    const actions = buildSceneActions(facade, dispatchEvent);

    this.exerciseActor?.stop();
    this.exerciseActor = createActor(exerciseMachine.provide({ actions }), {
      input: {
        totalObjectives: config.objectives.length,
        chapterIdx: config.chapterIdx,
      },
    });
    this.exerciseActor.start();

    // ---- Route EventBus events through the machine ----
    ReactToPhaserListeners.StartExercise(
      bind('StartExercise', () => {
        if (isCapacitorNative()) {
          console.log('[EXERCISE-DBG] ExerciseBase StartExercise calling MuseFlowAudio.warmUp()');
          MuseFlowAudio.warmUp().then(() => {
            this.dispatchExerciseEvent({ type: 'START_EXERCISE' });
          });
        } else {
          this.dispatchExerciseEvent({ type: 'START_EXERCISE' });
        }
      }),
      this,
    );
    ReactToPhaserListeners.PauseExercise(
      bind('PauseExercise', () => {
        this.dispatchExerciseEvent({ type: 'PAUSE_EXERCISE' });
      }),
      this,
    );
    ReactToPhaserListeners.MidiOn(
      bind('MidiOn', (data) => this.midiOn(data)),
      this,
    );
    ReactToPhaserListeners.MidiOff(
      bind('MidiOff', (data) => this.midiOff(data)),
      this,
    );
    ReactToPhaserListeners.SkipExercise(
      bind('SkipExercise', () => {
        this.dispatchExerciseEvent({ type: 'SKIP_EXERCISE' });
      }),
      this,
    );
    ReactToPhaserListeners.UnloadExercise(
      bind('UnloadExercise', () => this.stop()),
      this,
    );
    ReactToPhaserListeners.ChangePlaybackRate(
      bind('ChangePlaybackRate', (data) => this.setPlaybackRate(data)),
      this,
    );
    ReactToPhaserListeners.ToggleExercise(
      bind('ToggleExercise', () => this.handleToggle()),
      this,
    );
    ReactToPhaserListeners.ResumeExercise(
      bind('ResumeExercise', () => {
        this.dispatchExerciseEvent({ type: 'RESUME_EXERCISE' });
      }),
      this,
    );
    ReactToPhaserListeners.UpdateObjective(
      bind('UpdateObjective', (data: { chapter: number }) => {
        this.dispatchExerciseEvent({ type: 'UPDATE_OBJECTIVE', chapter: data.chapter });
      }),
      this,
    );
  }

  // ==================================================================
  // IExerciseSceneFacade implementation (overridable by subclasses)
  // ==================================================================

  loadObjective(objectiveIndex: number): void {
    this.currentObjective = objectiveIndex;
    const objective = this.config?.objectives[objectiveIndex];
    if (objective) {
      this.showInstruction(objective.text ?? 'Play this line of music.');
    }
    // Subclasses override to build graphics, then call:
    // this.dispatchExerciseEvent({ type: 'OBJECTIVE_LOADED' });
    // Default: immediately loaded (subclass MUST override if async)
    this.dispatchExerciseEvent({ type: 'OBJECTIVE_LOADED' });
  }

  unloadObjective(): void {}

  centreHeight(objectHeight: number): number {
    return this.cameras.main.height / 2 - objectHeight / 2;
  }

  centreWidth(objectWidth: number): number {
    return this.cameras.main.width / 2 - objectWidth / 2;
  }

  initExercise(_objectiveIndex: number): void {
    // Subclasses override to create cursor, event stream, start metronome
    this.startExercise();
  }

  beginPlayback(_note: number, _velocity: number): void {}

  stopPlayback(): void {}

  evaluatePerformance(): void {
    // Default: pass (subclasses override to check notes)
    this.dispatchExerciseEvent({ type: 'OBJECTIVE_PASSED' });
  }

  notifyShowExercise(): void {
    NotifyReactEvents.ShowExercise(undefined);
  }

  notifyHideExercise(): void {
    console.log('[EXERCISE-DBG] notifyHideExercise');
    NotifyReactEvents.HideExercise();
  }

  notifyExercisePassed(): void {
    console.log(`[EXERCISE-DBG] notifyExercisePassed skipped=${this.skipped}`);
    if (!this.skipped) {
      NotifyReactEvents.ExercisePassed();
    }
  }

  notifyObjectiveComplete(objectiveIndex: number, isLastObjective: boolean): void {
    console.log(
      `[EXERCISE-DBG] notifyObjectiveComplete idx=${objectiveIndex} isLast=${isLastObjective}`,
    );
    const objective = this.config?.objectives[objectiveIndex];
    if (objective?.videoSkipDestination && isLastObjective) {
      console.log(
        `[EXERCISE-DBG] firing ObjectiveComplete jumpTo=${objective.videoSkipDestination}`,
      );
      NotifyReactEvents.ObjectiveComplete({ jumpTo: objective.videoSkipDestination! });
    }
  }

  notifyObjectiveSkipped(objectiveIndex: number, isLastObjective: boolean): void {
    const objective = this.config?.objectives[objectiveIndex];
    if (objective?.videoSkipDestination) {
      // `isLastObjective` lets React distinguish a within-exercise objective
      // skip (advance the video, the running scene's machine loads the next
      // objective) from a skip that ends the exercise and jumps to the next
      // chapter. The latter must route through the robust chapter-jump path
      // so the next exercise's scene is loaded before playback resumes —
      // otherwise the next chapter's UpdateObjective fires before its scene
      // exists and the exercise never loads.
      NotifyReactEvents.ObjectiveSkipped({
        jumpTo: objective.videoSkipDestination,
        isLastObjective,
      });
    }
  }

  requestSkipButton(): void {
    NotifyReactEvents.SkipButtonRequest(undefined);
  }

  showPassAnimation(onComplete: () => void): void {
    if (!isCapacitorNative()) {
      if (this.sound.locked) {
        this.sound.unlock();
      }
      Tone.start();
    }

    const clipIdx = this.registry.get('clipIdx') as number;
    this.registry.set(
      'clipIdx',
      clipIdx + 1 >= this.registry.get('feedbackClipCount') ? 0 : clipIdx + 1,
    );

    this.instruction?.showResult(true, () => {
      const clipKey = this.registry.get('feedbackClipRandomDistribution')[clipIdx];
      if (isCapacitorNative()) {
        const clipName = feedbackKeyToFilename(clipKey);
        MuseFlowAudio.playSoundEffect({ name: clipName }).then(({ duration }) => {
          this.time.delayedCall(feedbackAdvanceDelayMs(duration), () => onComplete());
        });
      } else {
        // MUS-113: wait the clip's real duration (floored) before advancing,
        // mirroring the native branch, so a clip longer than the old hardcoded
        // 1000ms is no longer cut off when the scene tears down. Use add()
        // rather than play(key) to read the decoded duration, and destroy the
        // instance on completion since add() does not auto-remove it.
        const clip = this.sound.add(clipKey);
        clip.once(Phaser.Sound.Events.COMPLETE, () => clip.destroy());
        clip.play();
        const durationSecs = clip.duration > 0 ? clip.duration : FEEDBACK_DURATION_FALLBACK_SECS;
        this.time.delayedCall(feedbackAdvanceDelayMs(durationSecs), () => onComplete());
      }
    });
  }

  showFailAnimation(_failedAttempts: number): void {
    this.failExercise();
  }

  pauseScene(): void {
    this.paused = true;
    this.pauseExercise();
  }

  resumeScene(): void {
    this.paused = false;
    this.resume();
  }

  playNarration(objectiveIndex: number): void {
    if (!this.config) return;
    const objective = this.config.objectives[objectiveIndex];
    if (!objective?.narrationStartTimestamp || !objective?.narrationEndTimestamp) {
      // No narration timestamps — advance immediately
      this.dispatchExerciseEvent({ type: 'NARRATION_COMPLETE' });
      return;
    }

    const startSecs = this.timestampToSeconds(objective.narrationStartTimestamp);
    const endSecs = this.timestampToSeconds(objective.narrationEndTimestamp);
    const durationMs = (endSecs - startSecs) * 1000;

    if (isCapacitorNative() && this.config.levelNumber !== undefined) {
      const rate = this.config.playbackSpeed ?? 1;
      console.log(
        `[EXERCISE-DBG] playNarration file=level-${this.config.levelNumber}-audio start=${startSecs} end=${endSecs} duration=${durationMs}ms rate=${rate}`,
      );
      MuseFlowAudio.playNarration({
        file: `level-${this.config.levelNumber}-audio`,
        startTime: startSecs,
        endTime: endSecs,
        rate,
      });
    }

    // Schedule NARRATION_COMPLETE after the narration duration (adjusted
    // for playback speed). On web, START_EXERCISE from the video may
    // arrive first — that's fine, the machine accepts both.
    const rate = this.config.playbackSpeed ?? 1;
    const adjustedDuration = durationMs / rate;
    this.time.delayedCall(Math.max(adjustedDuration, 500), () => {
      this.dispatchExerciseEvent({ type: 'NARRATION_COMPLETE' });
    });
  }

  cleanup(): void {
    this.unloadGraphics();
    this.clearEvents();
  }

  unloadGraphics(): void {
    // Subclasses override to destroy scene-specific graphics
  }

  scheduleStop(delay: number = 500): void {
    console.log(`[EXERCISE-DBG] scheduleStop delay=${delay}`);
    this.time.delayedCall(delay, this.stop, [], this);
  }

  // ==================================================================
  // Facade factory — creates the facade bound to this scene
  // ==================================================================

  private createFacade(): IExerciseSceneFacade {
    return {
      loadObjective: (idx) => this.loadObjective(idx),
      unloadObjective: () => this.unloadObjective(),
      initExercise: (idx) => this.initExercise(idx),
      beginPlayback: (note, velocity) => this.beginPlayback(note, velocity),
      stopPlayback: () => this.stopPlayback(),
      evaluatePerformance: () => this.evaluatePerformance(),
      notifyShowExercise: () => this.notifyShowExercise(),
      notifyHideExercise: () => this.notifyHideExercise(),
      notifyExercisePassed: () => this.notifyExercisePassed(),
      notifyObjectiveComplete: (idx, isLast) => this.notifyObjectiveComplete(idx, isLast),
      notifyObjectiveSkipped: (idx, isLast) => this.notifyObjectiveSkipped(idx, isLast),
      requestSkipButton: () => this.requestSkipButton(),
      showPassAnimation: (cb) => this.showPassAnimation(cb),
      showFailAnimation: (attempts) => this.showFailAnimation(attempts),
      pauseScene: () => this.pauseScene(),
      resumeScene: () => this.resumeScene(),
      playNarration: (idx) => this.playNarration(idx),
      cleanup: () => this.cleanup(),
      unloadGraphics: () => this.unloadGraphics(),
      scheduleStop: (delay) => this.scheduleStop(delay),
    };
  }

  // ==================================================================
  // Legacy methods (kept for subclass compatibility during migration)
  // ==================================================================

  onExerciseShow() {}

  /** @deprecated Use machine transitions instead */
  transition(_skip?: boolean, _last?: boolean): void {}

  midiOn(_data?: { note: number; velocity?: number }): void {}
  midiOff(_data?: { note: number }): void {}

  clearEvents(): void {
    this.removeOwnListener('MidiOn');
    this.removeOwnListener('MidiOff');
    this.removeOwnListener('PauseExercise');
    this.removeOwnListener('UpdateObjective');
    this.removeOwnListener('SkipExercise');
    this.removeOwnListener('UnloadExercise');
    this.removeOwnListener('ToggleExercise');
    this.removeOwnListener('ResumeExercise');
  }

  protected removeOwnListener(eventName: string): void {
    const fn = this.boundCallbacks.get(eventName);
    if (fn) {
      EventBus.off(eventName, fn, this);
      this.boundCallbacks.delete(eventName);
    }
  }

  unload(): void {
    console.log('[EXERCISE-DBG] ExerciseBase.unload — stopping actor');
    const wasActive = this.active;
    this.exerciseActor?.stop();
    this.exerciseActor = null;
    this.currentObjective = undefined;
    this.active = false;
    this.removeOwnListener('StartExercise');
    this.removeOwnListener('UpdateObjective');
    this.debugText?.destroy();
    this.debugText = null;
    this.active = false;
    this.failMessage?.destroy();
    this.failMessage = null;
    this.toastMessage?.destroy();
    this.toastMessage = null;
    this.instruction?.destroy();
    this.instruction = null;
    this.registeredKeys.clear();
    this.numFailedAttempts = 0;
    if (this.videoResumeTimeout) {
      this.videoResumeTimeout.destroy();
      this.videoResumeTimeout = undefined;
    }
    this.clearEvents();
    this.time.removeAllEvents();
    this.tweens.killAll();
    if (this.paused) this.scene.stop('PauseMenu');
  }

  // ---- Methods subclasses still use ----

  startExercise(): void {}

  pauseExercise(): void {
    this.paused = true;
    this.scene.launch('PauseMenu');
    NotifyReactEvents.ExercisePaused({});
  }

  resume(): void {
    this.paused = false;
    NotifyReactEvents.ExerciseResumed();
  }

  handleToggle(): void {
    if (this.paused) {
      this.dispatchExerciseEvent({ type: 'RESUME_EXERCISE' });
    } else {
      this.dispatchExerciseEvent({ type: 'PAUSE_EXERCISE' });
    }
  }

  onObjectivePassed(): void {}

  onObjectiveSkipped(): void {}

  onExerciseFail(): void {
    this.restart();
  }

  onExercisePass(): void {}

  restart(): void {}

  onExerciseOver(delay: number = 500): void {
    this.time.delayedCall(delay, this.stop, [], this);
  }

  abstract stop(): void;
  abstract create(): void;

  showToast(text: string): void {
    if (this.waitToast) return;
    this.waitToast = true;
    const dpr = 1;
    this.toastMessage?.destroy();
    this.toastMessage = this.add.text(100, 100, text, {
      fontFamily: 'Lato',
      fontSize: `${32 * dpr}px`,
      color: '#000000',
      align: 'center',
    });
    this.toastMessage.setAlpha(0);
    this.toastMessage.setPosition(
      this.cameras.main.width / 4 - this.toastMessage.width / 2,
      this.cameras.main.height / 4 - this.toastMessage.height / 2,
    );
    this.tweens.add({
      targets: this.toastMessage,
      alpha: 1,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        this.tweens.add({
          targets: this.toastMessage,
          alpha: 0,
          duration: 300,
          delay: 750,
          ease: 'Power2',
          onComplete: () => {
            this.waitToast = false;
          },
          callbackScope: this,
        });
      },
      callbackScope: this,
    });
  }

  failExercise(message?: string): void {
    this.failMessage?.destroy();
    const dpr = 1;
    const camW = this.cameras.main.width;
    const camH = this.cameras.main.height;
    this.failMessage = this.add.text(0, 0, message ?? 'Try again!', {
      fontFamily: 'Lato',
      fontSize: `${Math.min(48, camH * 0.1) * dpr}px`,
      color: '#000',
      fontStyle: 'bold',
    });
    this.failMessage.setPosition(
      camW / 2 - this.failMessage.width / 2,
      camH * 0.35 - this.failMessage.height / 2,
    );
    this.failMessage.setAlpha(0);
    this.tweens.add({
      targets: this.failMessage,
      alpha: 1,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        this.tweens.add({
          targets: this.failMessage,
          alpha: 0,
          duration: 500,
          delay: 1000,
          ease: 'Power2',
          onComplete: () => {
            this.dispatchExerciseEvent({ type: 'RETRY' });
          },
          callbackScope: this,
        });
      },
      callbackScope: this,
    });
  }

  /** @deprecated — subclasses should override passObjective behavior via facade */
  passObjective(_last?: boolean): void {
    this.dispatchExerciseEvent({ type: 'OBJECTIVE_PASSED' });
  }

  skipExercise(): void {
    this.dispatchExerciseEvent({ type: 'SKIP_EXERCISE' });
  }
}
